import { DEFAULT_TTS_PROFILE, type TtsProfile } from '@core/tts/ttsProfile'

import { API_BASE_URL, buildHeaders } from './ttsApi'

let cachedProfile: TtsProfile | null = null
let inflightProfile: Promise<TtsProfile> | null = null

/** Call after the user changes TTS preferences so the next fetch picks up server defaults. */
export function clearTtsProfileClientCache(): void {
  cachedProfile = null
  inflightProfile = null
}

function coerceProfile(payload: unknown): TtsProfile {
  const data = (payload as { data?: unknown })?.data ?? payload
  const base = DEFAULT_TTS_PROFILE
  const profile = data as Partial<TtsProfile>

  const voice = typeof profile.voice === 'string' && profile.voice.trim().length > 0 ? profile.voice : base.voice
  const playbackRate =
    typeof profile.playbackRate === 'number' && profile.playbackRate > 0 ? profile.playbackRate : base.playbackRate
  const outputFormat = profile.outputFormat === 'wav' || profile.outputFormat === 'mp3' ? profile.outputFormat : base.outputFormat
  const speed = typeof profile.speed === 'number' && profile.speed > 0 ? profile.speed : base.speed

  return {
    voice,
    playbackRate,
    outputFormat,
    ...(typeof speed === 'number' ? { speed } : {}),
  }
}

export async function getTtsProfileClient(): Promise<TtsProfile> {
  if (cachedProfile) return cachedProfile
  if (inflightProfile) return inflightProfile

  inflightProfile = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tts/profile`, {
        method: 'GET',
        headers: buildHeaders(),
      })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const payload = await response.json()
      const resolved = coerceProfile(payload)
      cachedProfile = resolved
      return resolved
    } catch (error) {
      console.warn('[TTS] Failed to load profile, using defaults.', error)
      cachedProfile = DEFAULT_TTS_PROFILE
      return cachedProfile
    } finally {
      inflightProfile = null
    }
  })()

  return inflightProfile
}
